import { useEffect, useState } from 'react'
import { parseNotices, type NoticeEntry, type NoticeGroup } from '../../lib/notices'

/**
 * THE CREDITS PAGE.
 *
 * Two layers, and the split is the design:
 *
 *   1. THE PROJECTS A READER WOULD RECOGNISE, named with what each one actually
 *      does for the artist reading it. This is the part that reads like thanks.
 *   2. EVERY PACKAGE, all 159 of them across three applications, with licence
 *      text. This is the part that IS thanks — the complete acknowledgement,
 *      because crediting only the famous ones is crediting nobody who needed it.
 *
 * The generated artifact is produced by `wavbee/studio`'s
 * `scripts/gen-third-party-notices.mjs`, which reads BOTH repositories'
 * manifests — the studio SPA, this platform's gems, and this platform's
 * JavaScript. One generator, one file, so the two apps cannot credit different
 * things.
 */

// Committed under `public/`, not built at request time, so dev and production
// serve identical bytes.
const NOTICES_URL = '/third-party-notices.txt'

interface Maintainer {
  name: string
  url: string
  role: string
}

interface StackItem {
  name: string
  license: string
  url: string
  note: string
}

interface StackGroup {
  group: string
  blurb: string
  items: StackItem[]
}

interface Props {
  maintainers: Maintainer[]
  stack: StackGroup[]
  stack_count: number
}

type NoticesState =
  | { status: 'loading' }
  | { status: 'loaded'; groups: NoticeGroup[] }
  | { status: 'unavailable' }

/**
 * `res.ok` IS NOT ENOUGH. `/third-party-notices.txt` is not a route this app
 * owns, so a missing artifact is not answered with a 404 — the server falls
 * through to the HTML shell at HTTP 200, and the page would render the doctype
 * as a package name. `parseNotices` returns null for anything without the
 * generator's own marker line, and that is the only check that can tell the
 * difference; the transport cannot.
 */
function useNotices(): NoticesState {
  const [state, setState] = useState<NoticesState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    fetch(NOTICES_URL)
      .then((res) => {
        if (!res.ok) throw new Error('NOTICES_UNAVAILABLE')
        return res.text()
      })
      .then((text) => {
        if (cancelled) return
        const groups = parseNotices(text)
        setState(groups && groups.length > 0 ? { status: 'loaded', groups } : { status: 'unavailable' })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'unavailable' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  return state
}

const LABEL = 'text-xs font-semibold uppercase tracking-[0.14em] text-white/45'

/**
 * COLLAPSED, and the collapse is deliberate. 159 packages of licence text is a
 * wall nobody reads and nobody can find their own package in. `<details>`
 * rather than React state because the body must stay IN THE DOM while hidden —
 * that is what keeps a licence findable by the browser's own in-page search,
 * which is how anyone realistically looks for one.
 */
function NoticeRow({ entry }: { entry: NoticeEntry }) {
  return (
    <details className="border-b border-white/[0.06] py-3">
      <summary className="cursor-pointer list-item marker:text-white/30">
        <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1.5 align-middle">
          <span className="text-sm font-medium text-white">{entry.name}</span>
          {entry.version && <span className="text-xs tabular-nums text-white/40">{entry.version}</span>}
          {entry.license && (
            <span className="rounded border border-white/15 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-[0.08em] text-white/45">
              {entry.license}
            </span>
          )}
          {entry.attribution && <span className="text-xs text-white/45">{entry.attribution}</span>}
        </span>
      </summary>
      {entry.text && (
        <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-black/30 p-4 text-[0.6875rem] leading-relaxed text-white/50">
          {entry.text}
        </pre>
      )}
    </details>
  )
}

export default function Credits({ maintainers, stack, stack_count }: Props) {
  const notices = useNotices()
  const total =
    notices.status === 'loaded' ? notices.groups.reduce((n, g) => n + g.entries.length, 0) : null

  return (
    <div className="mx-auto max-w-4xl px-6 py-16">
      {/* THE THANKS, FIRST. Everything below is the evidence for it. */}
      <header>
        <p className={LABEL}>Credits</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight text-white">
          Standing on other people&rsquo;s work
        </h1>

        <div className="mt-6 max-w-2xl space-y-4 text-[0.9375rem] leading-relaxed text-white/70">
          <p>
            Almost none of this was invented by us. Your music moves through code written by
            people we have never met, in countries we have never visited, and given away for
            nothing.
          </p>
          <p>
            Most of it was written in evenings and on weekends. Much of it is maintained by one
            or two people who hear from strangers mainly when something breaks, and who keep
            going anyway — for years, often for a decade or more, usually unpaid. Somebody
            fixed a bug at midnight so that an artist they will never hear of could upload a
            song this morning.
          </p>
          <p>
            That is the deal open source makes: take this, build something, and pass it on.
            wavbee exists because thousands of people kept their side of it. The least we can do
            is say so by name — <strong className="text-white/85">all</strong> of them, not only
            the famous ones.
          </p>
        </div>
      </header>

      {/* WHO BUILDS THIS. High on the page: this is wavbee's own site, so there
          is no reason to be coy about it. */}
      <section className="mt-14 rounded-lg border border-white/10 bg-white/[0.02] p-6">
        <p className={LABEL}>Platform</p>
        <h2 className="mt-3 text-lg font-semibold text-white">Who builds and maintains it</h2>
        <dl className="mt-5 space-y-4">
          {maintainers.map((m) => (
            <div key={m.name}>
              <dt>
                <a
                  href={m.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#ff6a1a] hover:underline"
                >
                  {m.name}
                </a>
              </dt>
              <dd className="mt-1 text-sm leading-relaxed text-white/60">{m.role}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* THE HEAVY LIFTERS — grouped by the job they do for an artist rather
          than by language, which is the only grouping that means anything to
          the person whose catalogue depends on them. */}
      <div className="mt-14">
        <p className={LABEL}>The heavy lifting</p>
        <h2 className="mt-3 text-2xl font-semibold text-white">
          {stack_count} projects doing most of the work
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60">
          If you write software, several of these are worth sponsoring. They hold up far more
          of the internet than they get credit for.
        </p>
      </div>

      {stack.map((group) => (
        <section key={group.group} className="mt-10">
          <h3 className="text-lg font-semibold text-white">{group.group}</h3>
          <p className="mt-1.5 text-sm text-white/55">{group.blurb}</p>

          <ul className="mt-5 divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {group.items.map((item) => (
              <li key={item.name} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-3.5">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-white hover:text-[#ff6a1a] hover:underline"
                >
                  {item.name}
                </a>
                <span className="rounded border border-white/15 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-[0.08em] text-white/45">
                  {item.license}
                </span>
                <span className="w-full text-sm leading-relaxed text-white/60 sm:w-auto sm:flex-1">
                  {item.note}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {/* EVERY PACKAGE. The section above is the readable thanks; this is the
          complete one. Nothing we use goes uncredited. */}
      <section className="mt-16 border-t border-white/10 pt-12">
        <p className={LABEL}>Everything</p>
        <h2 className="mt-3 text-2xl font-semibold text-white">
          Every package we use{total !== null && <span className="text-white/40"> · {total}</span>}
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60">
          The full list across all three applications — this platform&rsquo;s server, its
          interface, and the artist studio our partner labels run. Each entry opens to its
          licence in full. We would rather over-credit than leave anyone out.
        </p>

        {notices.status === 'loading' && (
          <p className="mt-8 text-sm text-white/40">Loading the full list&hellip;</p>
        )}

        {/* SAYING SO, rather than rendering an empty list. A page claiming zero
            dependencies is a different untruth, not a fallback. */}
        {notices.status === 'unavailable' && (
          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-white/50">
            The complete list could not be loaded just now. It is not missing — write to{' '}
            <a href="mailto:info@wavbee.com" className="text-[#ff6a1a] hover:underline">
              info@wavbee.com
            </a>{' '}
            and we will send it to you.
          </p>
        )}

        {notices.status === 'loaded' &&
          notices.groups.map((group) => (
            <div key={group.name ?? 'ungrouped'} className="mt-10">
              {group.name && <h3 className="text-base font-semibold text-white">{group.name}</h3>}
              {group.description && (
                <p className="mt-1.5 max-w-2xl text-sm text-white/55">{group.description}</p>
              )}
              <div className="mt-4">
                {group.entries.map((entry) => (
                  <NoticeRow key={`${group.name}-${entry.name}-${entry.version}`} entry={entry} />
                ))}
              </div>
            </div>
          ))}
      </section>

      <section className="mt-14 border-t border-white/10 pt-10">
        <p className="max-w-2xl text-[0.9375rem] leading-relaxed text-white/60">
          To everyone whose name is on this page, and to the many whose names are only in a
          commit log somewhere: thank you. We know what you gave away.
        </p>
      </section>
    </div>
  )
}
